// @givmo/board — the small pieces every calendar card and dialog shares (CBG-CAL1 slice 1d).
//
// Each composes the design system's own primitives and Board's own fine print (`HowThisWorks`,
// `StatusWord`) in the type scale the shipped Board cards use (the retention card's header, "Who's
// coming"'s rows), so the new cards read as the same system as the ones beside them — the approved
// frames' pieces, built from the mockup harness at `design/board-calendar-mockups` @ `ad223ba7`.

import type { ReactNode } from 'react';
import { cn, testIdProp } from '@givmo/design-system';
import { HowThisWorks, StatusWord } from '../../components/fine-print';
import { calendarWords as w, providerName } from '../../calendar/calendar-words';
import type { MeetingCalendar } from '../meetings/calendar-decode';

export const TITLE = 'font-sans text-s16 font-w600 tracking-givmo text-text-strong';
export const LEDE =
  'mt-2 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
export const BODY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
export const SMALL = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
export const NOTE = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
export const STRONG = 'font-sans text-s14 font-w600 tracking-givmo text-text-strong';
export const DIALOG_TITLE = 'font-sans text-s18 font-w700 tracking-givmo text-text-strong';
/** A line that must be read before the button: the body's size, a step stronger. */
export const STRONG_LINE =
  'font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong';
/** The one coloured tone: a state someone must act on. */
export const ACT = 'font-sans text-s14 font-w600 tracking-givmo text-warning-700';

/** Codes whose words the frames draw as "Board can't reach …"; every other reads as its own (B2). */
export const RECONNECT_CODES: ReadonlySet<string> = new Set(['reconnect_required']);

/**
 * A meeting's line when its link needs attention, by the link's code: the amber line on its
 * Overview, and what a move that didn't finish says of a meeting that moved (CBG-FIX36). Every code
 * with no drawn words reads "Something needs attention" (B2). `ownerName` names a departed organizer;
 * without one, the address stands in.
 */
export function attentionLine(
  block: Pick<MeetingCalendar, 'needs_attention_code' | 'provider' | 'organizer_email'>,
  ownerName?: string,
): string {
  const code = block.needs_attention_code ?? '';
  const provider = providerName(block.provider);
  const email = block.organizer_email ?? '';
  if (code === 'organizer_left') return w.attention.organizer_left(ownerName ?? email);
  if (code === 'restored') return w.attention.restored(provider);
  if (code === 'event_missing') return w.attention.event_missing(provider);
  if (code === 'marker_missing') return w.attention.marker_missing(provider);
  if (RECONNECT_CODES.has(code)) return w.attention.reconnect_required(email);
  if (code === 'old_invitation_not_cancelled') return w.attention.old_invitation_not_cancelled;
  return w.attention.other;
}

/** A card header's left block: the title, one line (or none), and the fine print behind "How this works". */
export function CardTitle({
  title,
  lede,
  how,
  howTestId,
}: {
  title: string;
  lede: ReactNode;
  how?: readonly ReactNode[];
  howTestId?: string;
}) {
  return (
    <div className="min-w-0">
      <h3 className={TITLE}>{title}</h3>
      {lede !== null || how !== undefined ? (
        <p className={LEDE}>
          {lede}
          {how !== undefined ? (
            <>
              {' '}
              <HowThisWorks testId={howTestId ?? 'calendar-how'} lines={how} />
            </>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

/**
 * One account or person on a card: who, then the quiet facts, a status only when someone must act,
 * and the row's own action at the right. A text button drops under the words on a phone; a "⋯" menu
 * stays at the row's right edge at every width.
 */
export function AccountRow({
  primary,
  secondary,
  status,
  note,
  action,
  menu,
  testId,
}: {
  primary: ReactNode;
  /** The quiet facts, each kept whole when the line wraps (an address may break; a count never). */
  secondary?: readonly ReactNode[];
  /** A status word, only when someone must act (amber); never one that restates the default. */
  status?: string;
  /** A line under the row saying what the status means. */
  note?: ReactNode;
  /** A text button (or two): under the words on a phone, at the right from `sm`. */
  action?: ReactNode;
  /** A "⋯" menu: at the right edge at every width. */
  menu?: ReactNode;
  testId: string;
}) {
  return (
    <li
      className="flex items-start gap-12 px-20 py-14 sm:items-center sm:gap-16"
      {...testIdProp(testId)}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-8 sm:flex-row sm:items-center sm:gap-16">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <span className="flex flex-wrap items-baseline gap-x-10 gap-y-2">
            <span className={cn(STRONG, 'min-w-0 break-words')}>{primary}</span>
            {status !== undefined ? (
              <StatusWord tone="act" testId={`${testId}-status`}>
                {status}
              </StatusWord>
            ) : null}
          </span>
          {secondary !== undefined && secondary.length > 0 ? (
            <span className={SMALL}>
              {/* Each part keeps its own separator at its END, so a wrapped line never starts with
                  "·"; an address may break, a count never does. */}
              {secondary.map((part, index) => (
                <span key={index}>
                  <span className={index === 0 ? 'break-words' : 'whitespace-nowrap'}>
                    {part}
                    {index < secondary.length - 1 ? ' ·' : null}
                  </span>
                  {index < secondary.length - 1 ? ' ' : null}
                </span>
              ))}
            </span>
          ) : null}
          {note !== undefined ? <span className={cn(NOTE, 'mt-4')}>{note}</span> : null}
        </div>
        {action !== undefined ? (
          <div className="flex shrink-0 flex-wrap items-center gap-8">{action}</div>
        ) : null}
      </div>
      {menu !== undefined ? <div className="-my-4 flex shrink-0 items-center">{menu}</div> : null}
    </li>
  );
}

/** "Oct 20": a meeting's day in the meeting's own zone (the rows' and the history's date column). */
export function meetingDay(meeting: {
  starts_at?: string | null;
  time_zone?: string | null;
}): string {
  const iso = meeting.starts_at;
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: meeting.time_zone ?? undefined,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
  }
}

/** "Tue, Oct 20": a meeting's weekday and day in its own zone, for a dialog's list. */
export function meetingWeekday(meeting: {
  starts_at?: string | null;
  time_zone?: string | null;
}): string {
  const iso = meeting.starts_at;
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const options: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };
  try {
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      timeZone: meeting.time_zone ?? undefined,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', options).format(date);
  }
}

/** "Oct 3": an instant's day in the reader's zone (a reply, a removal, a history row). */
export function instantDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
}

/** Personal mail services: never a board account's suggested domain. */
const PERSONAL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
]);

/** The first address's domain that isn't a personal mail service's, or null. */
export function organizationDomain(
  addresses: readonly (string | null | undefined)[],
): string | null {
  for (const address of addresses) {
    const domain = address?.split('@')[1]?.trim().toLowerCase();
    if (domain && domain.includes('.') && !PERSONAL_DOMAINS.has(domain)) return domain;
  }
  return null;
}
